import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Alert, ListGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import './MazeGame.css';

// Game configuration constants
const MAZE_WIDTH = 10;
const MAZE_HEIGHT = 10;
const WALL_FREQUENCY = 0.2; // 20% of cells are walls (reduced to create more paths)
const ENEMY_MOVE_INTERVAL = 3000; // Enemy moves every 3 seconds

// Define Question type
const sampleQuestions = [
  {
    id: 1,
    question: "Which element has the symbol 'Na'?",
    correct_answer: "Sodium",
    options: ["Nitrogen", "Sodium", "Neon", "Nickel"],
    category: "chemistry",
    difficulty: "easy"
  },
  {
    id: 2,
    question: "What is the value of π (pi) to two decimal places?",
    correct_answer: "3.14",
    options: ["3.14", "3.15", "3.16", "3.17"],
    category: "math",
    difficulty: "easy"
  },
  {
    id: 3,
    question: "Which of the following is Newton's First Law?",
    correct_answer: "An object in motion stays in motion unless acted upon by an external force",
    options: [
      "Force equals mass times acceleration",
      "An object in motion stays in motion unless acted upon by an external force",
      "For every action, there is an equal and opposite reaction",
      "Energy cannot be created or destroyed"
    ],
    category: "physics",
    difficulty: "medium"
  },
  {
    id: 4,
    question: "What is the chemical formula for water?",
    correct_answer: "H₂O",
    options: ["CO₂", "H₂O", "NaCl", "CH₄"],
    category: "chemistry",
    difficulty: "easy"
  },
  {
    id: 5,
    question: "What is the powerhouse of the cell?",
    correct_answer: "Mitochondria",
    options: ["Nucleus", "Mitochondria", "Ribosome", "Endoplasmic Reticulum"],
    category: "biology",
    difficulty: "easy"
  },
  {
    id: 6,
    question: "Which data structure operates on a LIFO basis?",
    correct_answer: "Stack",
    options: ["Queue", "Linked List", "Stack", "Tree"],
    category: "computer science",
    difficulty: "medium"
  },
  {
    id: 7,
    question: "What is the period of a pendulum with length 1 meter on Earth?",
    correct_answer: "About 2 seconds",
    options: ["About 1 second", "About 2 seconds", "About 3 seconds", "About 4 seconds"],
    category: "physics",
    difficulty: "medium"
  },
  {
    id: 8,
    question: "Which algorithm has the worst time complexity?",
    correct_answer: "Bubble Sort - O(n²)",
    options: ["Binary Search - O(log n)", "Merge Sort - O(n log n)", "Bubble Sort - O(n²)", "Quick Sort - O(n log n)"],
    category: "computer science",
    difficulty: "hard"
  }
];

const MazeGame = () => {
  const { room } = useParams() || { room: 'default_room' };
  const location = useLocation();
  const access_token = useSelector((state) => state.userLogin?.userInfo?.access);
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  
  // WebSocket state
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState(null);
  const socketRef = useRef(null);
  
  // Use a unique room ID if not provided, but store it in a ref to prevent changing
  const roomNameRef = useRef(room || `maze_${Math.floor(Math.random() * 100000)}`);
  // Use the stored room name consistently
  const roomName = roomNameRef.current;
  
  // Topics state
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [loadingTopics, setLoadingTopics] = useState(false);
  
  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameResult, setGameResult] = useState(null); // "win" or "lose"
  const [playerName, setPlayerName] = useState("");
  const [gameJoined, setGameJoined] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [maze, setMaze] = useState([]);
  const [playerPosition, setPlayerPosition] = useState({ x: 0, y: 0 });
  const [enemyPosition, setEnemyPosition] = useState({ x: 0, y: 0 });
  const [exitPosition, setExitPosition] = useState({ x: 0, y: 0 });
  const [availableMoves, setAvailableMoves] = useState([]);
  
  // Question state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showingQuestion, setShowingQuestion] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [nextMove, setNextMove] = useState(null);
  const [questions, setQuestions] = useState([]);
  
  // Game timer
  const [time, setTime] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [enemyMoveTimer, setEnemyMoveTimer] = useState(null);

  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'lost'
  
  // Animation states
  const [showVictoryAnimation, setShowVictoryAnimation] = useState(false);
  const [showCapturedAnimation, setShowCapturedAnimation] = useState(false);
  const [showEscapeLight, setShowEscapeLight] = useState(false);
  const [showMonsterAttack, setShowMonsterAttack] = useState(false);
  const [shakingCell, setShakingCell] = useState(null);


// Fetch topics from API
useEffect(() => {
  const fetchTopics = async () => {
    setLoadingTopics(true);
    try {
      console.log('Fetching topics...');
      const response = await axios.get('https://api.andrewslearning.com/api/topics/');
      console.log('Topics API response:', response);
      setTopics(response.data);
      console.log('Topics loaded:', response.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setLoadingTopics(false);
    }
  };
  
  fetchTopics();
}, []);

  // Join the game
  const joinGame = useCallback(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('Socket not connected');
      return;
    }
    
    // Use Redux user info if available, otherwise use entered name
    const name = userInfo?.username || playerName;
    
    if (!name) {
      alert('Please enter your name to join the game!');
      return;
    }
    
    const joinMessage = {
      type: 'join_game',
      name: name,
      // Include player_id if we have one from a previous session
      ...(playerId && { player_id: playerId })
    };
    
    console.log('Joining game with:', joinMessage);
    socket.send(JSON.stringify(joinMessage));
  }, [socket, playerName, userInfo, playerId]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    // Close any existing connection
    if (socketRef.current) {
      socketRef.current.close();
    }

    // Get topic ID from query parameter if available or use the selected topic
    const searchParams = new URLSearchParams(location.search);
    const topicFromQuery = searchParams.get('topic');
    const topicId = topicFromQuery || selectedTopic;
    
    // Create query string - explicitly use 'topic' as the parameter name to match backend
    const topicParam = topicId ? `topic=${topicId}` : '';
    
    // Build WebSocket URL - Use secure protocol
    let wsUrl = `wss://api.andrewslearning.com/wschat/maze/${roomNameRef.current}/`;
    
    // Append query string if we have a topic
    if (topicParam) {
      wsUrl += `?${topicParam}`;
    }
    
    console.log('Connecting to WebSocket:', wsUrl);

    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
      setSocket(ws);
      
      // Add debug message to verify connection opened successfully
      console.log('WebSocket connection established - ready to send/receive messages');
    };
    
    ws.onclose = (e) => {
      console.log('WebSocket disconnected:', e);
      setConnected(false);
      setGameJoined(false);
      
      // Only attempt to reconnect if game is active and component is mounted
      if (!isGameOver && !ws.manuallyDisconnected) {
        setTimeout(() => {
          console.log('Attempting to reconnect WebSocket...');
          connectWebSocket();
        }, 3000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        console.log('WebSocket message received:', data);
        handleSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    // Add a custom property to track manual disconnections
    ws.manuallyDisconnected = false;
  }, [location.search, isGameOver, selectedTopic]);

  // Auto-join effect when socket connects and we have a player name
  useEffect(() => {
    if (socket && socket.readyState === WebSocket.OPEN && playerName && !gameJoined) {
      joinGame();
    }
  }, [socket, playerName, gameJoined, joinGame]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (playerName) {
      connectWebSocket();
    }
    
    return () => {
      // Clean up WebSocket connection
      if (socketRef.current) {
        // Mark as manually disconnected to prevent auto-reconnect
        socketRef.current.manuallyDisconnected = true;
        socketRef.current.close();
      }
    };
  }, [connectWebSocket, playerName]);
 useEffect(() => {
    if (isCorrect) {
      movePlayer();
    {/*setShowFeedback(false);
      setNextMove(null);*/}

    {/*     setIsCorrect(false);*/}
    }
  }, [isCorrect]);


  // Handle incoming socket messages
  const handleSocketMessage = (data) => {
    try {
      console.log('Received message:', data);
      
      switch (data.type) {
        case 'welcome_message':
          console.log('Received welcome message:', data.message);
          // We're connected to the socket but not yet joined a game
          setConnected(true);
          break;
          
        case 'player_list':
          console.log('Received player list:', data.players);
          setPlayers(data.players || []);
          break;
          
        case 'join_success':
          console.log('Join success:', data.player_id, data.player_name);
          setPlayerId(data.player_id);
          setPlayerName(data.player_name || playerName);
          setGameJoined(true);
          break;
          
        case 'player_joined':
          console.log('Player joined:', data.player_name);
          // Optionally: Show a toast or notification
          break;
          
        case 'player_left':
          console.log('Player left:', data.player_name);
          // Optionally: Update players list or show notification
          break;
          
        case 'new_question':
          console.log('Received new question:', data.question);
          // Process the question to normalize its format
          const questionData = data.question;
          
          if (!questionData) {
            console.error('Received empty question from server');
            // Show an error but don't use fallback yet (we already have a timeout)
            return;
          }
          
          // Check if it's a fallback question from the server
          const isFallbackQuestion = questionData.id && String(questionData.id).startsWith('fallback_');
          if (isFallbackQuestion) {
            console.warn('Server sent a fallback question - possible database issue');
          }
          
          // Ensure we have a proper format regardless of what the server sends
          const normalizedQuestion = {
            id: questionData.id || `local-${Date.now()}`,
            text: questionData.text || questionData.question || 'Answer this question',
            image: questionData.image || null,
            explanation: questionData.explanation || null
          };
          
          // Handle options in various formats
          if (Array.isArray(questionData.options)) {
            normalizedQuestion.options = questionData.options;
          } else if (Array.isArray(questionData.choices)) {
            // If options come as 'choices', rename to options for consistency
            normalizedQuestion.options = questionData.choices;
          } else if (!questionData.options && !questionData.choices) {
            // Create fallback options if none provided
            console.warn('No options found in question, creating defaults');
            normalizedQuestion.options = [
              { id: '1', text: 'Option 1' },
              { id: '2', text: 'Option 2' },
              { id: '3', text: 'Option 3' },
              { id: '4', text: 'Option 4' }
            ];
          }
          
          // Process the correct answer
          if (questionData.correct_answer) {
            normalizedQuestion.correct_answer = questionData.correct_answer;
          } else if (questionData.correct_option_index !== undefined && 
                    Array.isArray(normalizedQuestion.options)) {
            // If server provides index instead of value
            const correctOption = normalizedQuestion.options[questionData.correct_option_index];
            normalizedQuestion.correct_answer = typeof correctOption === 'object' 
              ? correctOption.text 
              : correctOption;
          }
          
          console.log('Normalized question:', normalizedQuestion);
          setCurrentQuestion(normalizedQuestion);
          setShowingQuestion(true);
          break;
          
        case 'answer_result':
          console.log('Answer result:', data);
          const isCorrect = data.correct;
          console.log("test",data.correct)
          setIsCorrect(isCorrect);
          
          // If we're already showing feedback, don't override it
          if (!showFeedback) {
            setShowFeedback(true);
          }
          
          // Handle the answer result after a delay
          setTimeout(() => {
            if (isCorrect) {
              // Move player if answer was correct
              movePlayer();
            }
            
            // Update scores if provided
            if (data.player_score) {
              setCorrectAnswers(Math.floor(data.player_score / 10)); // Assuming 10 points per correct answer
            }
            
            setShowingQuestion(false);
            setShowFeedback(false);
            setNextMove(null);
          }, 1500);
          break;
          
        case 'question_answered':
          // Another player answered a question
          console.log('Question answered by:', data.player_name);
          
          // Update player scores if provided
          if (data.scores) {
            const updatedPlayers = players.map(player => {
              const playerScore = data.scores[player.id];
              if (playerScore !== undefined) {
                return { ...player, score: playerScore };
              }
              return player;
            });
            setPlayers(updatedPlayers);
          }
          break;
          
        case 'game_started':
          console.log('Game started:', data.message);
          // Start the game on this client
          if (!gameStarted) {
            startGame();
          }
          break;
          
        case 'error':
          console.error('Received error from server:', data.message);
          // Optionally: Show error toast/alert
          break;
          
        default:
          console.log('Unhandled message type:', data.type);
      }
    } catch (error) {
      console.error('Error handling socket message:', error);
    }
  };

  // Start the game
  const startGame = () => {
    // Initialize a new maze
    const initialMaze = initializeMaze();
    setMaze(initialMaze);
    
    // Reset game state
    setIsGameOver(false);
    setGameResult(null);
    setCorrectAnswers(0);
    setWrongAnswers(0);
    setGameState('playing');
    setShowVictoryAnimation(false);
    setShowCapturedAnimation(false);
    setShowEscapeLight(false);
    setShowMonsterAttack(false);
    setShakingCell(null);
    
    // Start timer
    setTime(0);
    setTimerActive(true);
    
    // Start enemy movement timer
    if (enemyMoveTimer) {
      clearInterval(enemyMoveTimer);
    }
    
    console.log("Starting enemy movement timer");
    const enemyTimer = setInterval(() => {
      moveEnemySimple();
    }, ENEMY_MOVE_INTERVAL);
    
    setEnemyMoveTimer(enemyTimer);
    setGameStarted(true);
    
    // Notify server that game is started with selected topic
    if (socket?.readyState === WebSocket.OPEN) {
      console.log('Sending game start notification to server');
      socket.send(JSON.stringify({
        type: 'start_game',
        player_id: playerId,
        topic_id: selectedTopic || null,
        mode: 'single_player' // For future multiplayer support
      }));
    }
    
    // Update available moves for the initial position
    updateAvailableMoves(playerPosition, initialMaze);
  };
  
  // Request a question from the server
  const requestQuestion = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('Socket not connected, falling back to local questions');
      // Fall back to local questions
      const question = getRandomQuestion();
      setCurrentQuestion(question);
      setShowingQuestion(true);
      return;
    }
    
    console.log('Requesting question from server with playerId:', playerId);
    
    // Include player_id and topic for better server tracking
    const requestMessage = {
      type: 'request_next_question',
      player_id: playerId,
      topic_id: selectedTopic || undefined
    };
    
    // Debug log to track the request
    console.log('Sending question request:', requestMessage);
    
    // Show a loading state while waiting for response
    setShowingQuestion(true);
    setCurrentQuestion(null); // Clear current question until we get a response
    
    // Send the request to the server
    socket.send(JSON.stringify(requestMessage));
    
    // Set a timeout to fall back to local questions if server doesn't respond
    const fallbackTimeout = setTimeout(() => {
      if (!currentQuestion) {
        console.warn('Server question request timed out, using fallback question');
        const fallbackQuestion = getRandomQuestion();
        setCurrentQuestion(fallbackQuestion);
      }
    }, 5000); // 5 second timeout
    
    // Store the timeout ID for cleanup
    return () => clearTimeout(fallbackTimeout);
  };
  
  // Submit an answer to the server
  const submitAnswerToServer = (answer) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('Socket not connected');
      
      // Fall back to local answer checking
      handleSubmitAnswer(answer);
      return;
    }
    
    if (!currentQuestion) {
      console.error('No current question to answer');
      return;
    }
    
    console.log('Submitting answer to server:', answer);
    
    // First send a submission notification
    socket.send(JSON.stringify({
      type: 'player_submitted',
      player_id: playerId,
      player_name: playerName
    }));
    
    // Get the correct answer ID if available (for backend object-based answer checking)
    let answerId = null;
    
    // Log options for debugging
    console.log('Finding answer ID from options:', currentQuestion.options, 
                'or choices:', currentQuestion.choices);
    
    // Try to find the answer ID from options if they are objects
    const optionsArray = Array.isArray(currentQuestion.options) 
      ? currentQuestion.options 
      : (Array.isArray(currentQuestion.choices) ? currentQuestion.choices : []);
    
    if (optionsArray.length > 0) {
      // First, try to find the option using text match
      const selectedOption = optionsArray.find(opt => {
        if (typeof opt === 'object' && opt !== null) {
          return opt.text === answer || opt.value === answer;
        } 
        return opt === answer;
      });
      
      if (selectedOption && typeof selectedOption === 'object') {
        // Extract ID from the matched option
        answerId = selectedOption.id || selectedOption.option_id;
        console.log(`Found matching option with ID: ${answerId}`);
      } else {
        console.log(`No direct match found for answer: ${answer}`);
        
        // If no direct match, try finding by index (sometimes options are indexed)
        const index = optionsArray.findIndex(opt => {
          const optText = typeof opt === 'object' ? (opt.text || opt.value) : opt;
          return optText === answer;
        });
        
        if (index !== -1) {
          console.log(`Found answer at index ${index}`);
          // Some backends expect the index as the answer ID
          answerId = index.toString();
        }
      }
    }
    
    // Then send the actual answer after a short delay
    setTimeout(() => {
      const answerMessage = {
        type: 'submit_answer',
        question_id: currentQuestion.id,
        player_id: playerId
      };
      
      // Include as much information as possible to help the server
      if (answerId) {
        answerMessage.answer_id = answerId;
      }
      
      // Always include the text answer as a fallback
      answerMessage.answer = answer;
      
      // Add player name for easier server-side identification
      answerMessage.player_name = playerName;
      
      console.log('Sending answer message:', answerMessage);
      socket.send(JSON.stringify(answerMessage));
      
      // Show a loading state while waiting for response
      setShowFeedback(true);
    }, 300);
  };

  // Initialize the maze
  const initializeMaze = useCallback(() => {
    console.log("Initializing maze...");
    
    // Create empty maze
    let newMaze = [];
    
    // Create rows
    for (let y = 0; y < MAZE_HEIGHT; y++) {
      const row = [];
      // Create cells in each row
      for (let x = 0; x < MAZE_WIDTH; x++) {
        row.push({
          type: "empty",
          x,
          y
        });
      }
      newMaze.push(row);
    }
    
    // Add walls randomly
    for (let y = 0; y < MAZE_HEIGHT; y++) {
      for (let x = 0; x < MAZE_WIDTH; x++) {
        // Skip the corners (start, potential enemy spawn, and exit)
        if ((x === 0 && y === 0) || 
            (x === MAZE_WIDTH-1 && y === MAZE_HEIGHT-1) || 
            (x === 0 && y === MAZE_HEIGHT-1)) {
          continue;
        }
        
        // Add walls randomly
        if (Math.random() < WALL_FREQUENCY) {
          newMaze[y][x].type = "wall";
        }
      }
    }
    
    // Ensure the maze is solvable by creating a path
    for (let i = 0; i < MAZE_WIDTH - 1; i++) {
      newMaze[0][i].type = "empty"; // Top row path
      newMaze[i][MAZE_WIDTH - 1].type = "empty"; // Right column path
    }
    
    for (let i = 0; i < MAZE_HEIGHT - 1; i++) {
      newMaze[i][0].type = "empty"; // Left column path
      newMaze[MAZE_HEIGHT - 1][i].type = "empty"; // Bottom row path
    }
    
    // Set player at the start position (top-left)
    const playerPos = { x: 0, y: 0 };
    newMaze[playerPos.y][playerPos.x].type = "player";
    
    // Set enemy at the bottom-left corner
    const enemyPos = { x: 0, y: MAZE_HEIGHT - 1 };
    newMaze[enemyPos.y][enemyPos.x].type = "enemy";
    
    // Set exit at the bottom right
    const exitPos = { x: MAZE_WIDTH - 1, y: MAZE_HEIGHT - 1 };
    newMaze[exitPos.y][exitPos.x].type = "exit";
    
    // Update state with positions
    setPlayerPosition(playerPos);
    setEnemyPosition(enemyPos);
    setExitPosition(exitPos);
    
    return newMaze;
  }, []);

  // Get a random question (local fallback)
  const getRandomQuestion = () => {
    // Enhanced local questions for better fallback experience
    const localQuestions = [
      {
        id: "local_1",
        text: "What is the formula for the area of a circle?",
        options: [
          { id: "1", text: "πr²" },
          { id: "2", text: "2πr" },
          { id: "3", text: "πd" },
          { id: "4", text: "r²π" }
        ],
        correct_answer: "πr²",
        explanation: "The area of a circle is calculated using πr², where r is the radius of the circle.",
        category: "mathematics",
        difficulty: 0.3
      },
      {
        id: "local_2",
        text: "Which of the following is Newton's First Law of Motion?",
        options: [
          { id: "1", text: "F = ma" },
          { id: "2", text: "An object at rest stays at rest unless acted upon by an external force" },
          { id: "3", text: "For every action, there is an equal and opposite reaction" },
          { id: "4", text: "Energy cannot be created or destroyed" }
        ],
        correct_answer: "An object at rest stays at rest unless acted upon by an external force",
        explanation: "Newton's First Law states that an object will remain at rest or in uniform motion unless acted upon by an external force.",
        category: "physics",
        difficulty: 0.4
      },
      {
        id: "local_3",
        text: "What is the chemical formula for water?",
        options: [
          { id: "1", text: "H2O" },
          { id: "2", text: "CO2" },
          { id: "3", text: "O2" },
          { id: "4", text: "H2O2" }
        ],
        correct_answer: "H2O",
        explanation: "Water is composed of two hydrogen atoms and one oxygen atom, making its formula H2O.",
        category: "chemistry",
        difficulty: 0.2
      },
      {
        id: "local_4",
        text: "Which of these is a data structure that follows LIFO (Last In, First Out)?",
        options: [
          { id: "1", text: "Queue" },
          { id: "2", text: "Stack" },
          { id: "3", text: "Linked List" },
          { id: "4", text: "Binary Tree" }
        ],
        correct_answer: "Stack",
        explanation: "A stack is a data structure that follows LIFO - the last element added is the first one to be removed.",
        category: "computer science",
        difficulty: 0.5
      },
      {
        id: "local_5",
        text: "What is the most abundant gas in Earth's atmosphere?",
        options: [
          { id: "1", text: "Oxygen" },
          { id: "2", text: "Carbon Dioxide" },
          { id: "3", text: "Nitrogen" },
          { id: "4", text: "Argon" }
        ],
        correct_answer: "Nitrogen",
        explanation: "Nitrogen makes up approximately 78% of Earth's atmosphere.",
        category: "earth science",
        difficulty: 0.3
      }
    ];
    
    // Use the sample questions array if defined, otherwise use our enhanced local questions
    const questionsArray = sampleQuestions.length > 0 ? sampleQuestions : localQuestions;
    
    // Log the available questions for debugging
    console.log(`Using local questions (${questionsArray.length} available)`);
    
    // Return a random question from the array
    return questionsArray[Math.floor(Math.random() * questionsArray.length)];
  };
  
  // Update available moves for the player
  const updateAvailableMoves = (position, currentMaze) => {
    const directions = [
      { dx: 0, dy: -1 }, // up
      { dx: 1, dy: 0 },  // right
      { dx: 0, dy: 1 },  // down
      { dx: -1, dy: 0 }  // left
    ];
    
    const validMoves = directions.filter(dir => {
      const newX = position.x + dir.dx;
      const newY = position.y + dir.dy;
      
      return (
        newX >= 0 && newX < MAZE_WIDTH &&
        newY >= 0 && newY < MAZE_HEIGHT &&
        currentMaze[newY][newX].type !== "wall"
      );
    });
    
    setAvailableMoves(validMoves);
  };

  // Select a move (modified to use WebSocket)
  const selectMove = (dx, dy) => {
    if (showingQuestion) return;
    
    const moveDirection = { dx, dy };
    
    // Check if this move is available
    const isAvailable = availableMoves.some(
      move => move.dx === moveDirection.dx && move.dy === moveDirection.dy
    );
    
    if (!isAvailable) {
      console.log("Move not available");
      return;
    }
    
    // Set next move to be executed after answering question
    setNextMove(moveDirection);
    
    // Request a question from the server
    requestQuestion();
  };
  
  // Move player (no changes needed)
  const movePlayer = () => {
    if (!nextMove || isGameOver) return;
    
    const { dx, dy } = nextMove;
    const newX = playerPosition.x + dx;
    const newY = playerPosition.y + dy;
    
    // Create a copy of the maze
    const newMaze = maze.map(row => [...row]);
    
    // Check if the player reaches the exit
    if (newX === exitPosition.x && newY === exitPosition.y) {
      // Set cell shake animation on exit cell
      setShakingCell({ x: newX, y: newY });
      
      // Show escape light animation
      setShowEscapeLight(true);
      
      // Show victory animation with a slight delay
      setTimeout(() => {
        setShowVictoryAnimation(true);
      }, 500);
      
      // End game after animations
      setTimeout(() => {
        endGame("win");
      }, 1500);
      return;
    }
    
    // Update previous position
    newMaze[playerPosition.y][playerPosition.x].type = "empty";
    
    // Update new position
    newMaze[newY][newX].type = "player";
    
    // Update player position
    setPlayerPosition({ x: newX, y: newY });
    
    // Update available moves
    updateAvailableMoves({ x: newX, y: newY }, newMaze);
    
    // Check if enemy caught player
    if (newX === enemyPosition.x && newY === enemyPosition.y) {
      // Show the captured animation
      setShowCapturedAnimation(true);
      
      // End game with delay for visual effect
      setTimeout(() => {
        endGame("lose");
      }, 1200);
      return;
    }
    
    // Update the maze
    setMaze(newMaze);
    
    // Reset next move
    setNextMove(null);

    if (newX === exitPosition.x && newY === exitPosition.y) {
      setGameState('won');
    }
  };

  // Add useEffect to monitor maze changes and start/manage enemy movement
  useEffect(() => {
    // Only run if the game is started and maze is initialized
    if (gameStarted && maze.length > 0 && !isGameOver) {
      console.log("Maze changed or initialized, starting/updating enemy timer");
      
      // Clear any existing timer
      if (enemyMoveTimer) {
        console.log("Clearing existing timer:", enemyMoveTimer);
        clearInterval(enemyMoveTimer);
      }
      
      console.log("Creating a NEW enemy timer");
      
      // Create a new timer with a simple callback
      const timer = setInterval(() => {
        moveEnemySimple();
      }, ENEMY_MOVE_INTERVAL);
      
      // Store the timer ID so it can be cleared later
      setEnemyMoveTimer(timer);
      
      // Clean up on unmount or when dependencies change
      return () => {
        console.log("Cleaning up enemy timer");
        clearInterval(timer);
      };
    }
  }, [maze, gameStarted, isGameOver]);
  
  // Super simple enemy movement - just move toward player with some randomness
  const moveEnemySimple = () => {
    if (isGameOver || !maze || maze.length === 0) {
      console.log("Game over or maze not initialized - not moving enemy");
      return;
    }

    console.log("MOVING ENEMY - SIMPLE METHOD");
    console.log("Maze dimensions:", maze.length, "x", maze[0]?.length);
    
    try {
      // Get positions
      const enemyX = enemyPosition.x;
      const enemyY = enemyPosition.y;
      const playerX = playerPosition.x;
      const playerY = playerPosition.y;
      
      console.log("Enemy at:", enemyX, enemyY);
      console.log("Player at:", playerX, playerY);
      
      // Debug check for valid positions
      if (enemyY >= maze.length || enemyX >= maze[0].length || 
          playerY >= maze.length || playerX >= maze[0].length) {
        console.error("Invalid positions detected");
        return;
      }
      
      // Create a copy of the maze
      const newMaze = [...maze.map(row => [...row])];
      
      // Calculate direction to player
      const dirX = playerX - enemyX;
      const dirY = playerY - enemyY;
      
      // Possible moves
      let possibleMoves = [];
      
      // Add horizontal move if appropriate
      if (dirX !== 0) {
        // Direction to move (1 for right, -1 for left)
        const dx = dirX > 0 ? 1 : -1;
        
        // Check if valid
        const newX = enemyX + dx;
        if (
          newX >= 0 && newX < MAZE_WIDTH && 
          newMaze[enemyY][newX].type !== "wall" && 
          newMaze[enemyY][newX].type !== "exit"
        ) {
          // Add to possible moves, give higher weight if moving toward player
          possibleMoves.push({ 
            x: newX, 
            y: enemyY, 
            weight: 10 // Higher weight for moving toward player
          });
        }
      }
      
      // Add vertical move if appropriate
      if (dirY !== 0) {
        // Direction to move (1 for down, -1 for up)
        const dy = dirY > 0 ? 1 : -1;
        
        // Check if valid
        const newY = enemyY + dy;
        if (
          newY >= 0 && newY < MAZE_HEIGHT && 
          newMaze[newY][enemyX].type !== "wall" && 
          newMaze[newY][enemyX].type !== "exit"
        ) {
          // Add to possible moves, give higher weight if moving toward player
          possibleMoves.push({ 
            x: enemyX, 
            y: newY, 
            weight: 10 // Higher weight for moving toward player
          });
        }
      }
      
      // Add random adjacent moves for more unpredictability
      const directions = [
        { dx: 0, dy: -1 }, // up
        { dx: 1, dy: 0 },  // right
        { dx: 0, dy: 1 },  // down
        { dx: -1, dy: 0 }  // left
      ];
      
      for (const dir of directions) {
        const newX = enemyX + dir.dx;
        const newY = enemyY + dir.dy;
        
        // Skip if already added (moving toward player)
        if (possibleMoves.some(move => move.x === newX && move.y === newY)) {
          continue;
        }
        
        // Check if valid
        if (
          newX >= 0 && newX < MAZE_WIDTH && 
          newY >= 0 && newY < MAZE_HEIGHT && 
          newMaze[newY][newX].type !== "wall" && 
          newMaze[newY][newX].type !== "exit"
        ) {
          // Add as a possible move with lower weight
          possibleMoves.push({ 
            x: newX, 
            y: newY, 
            weight: 1 // Lower weight for random moves
          });
        }
      }
      
      // If no valid moves, stay in place
      if (possibleMoves.length === 0) {
        console.log("No valid moves for enemy");
        return;
      }
      
      // Select a move based on weights
      let totalWeight = possibleMoves.reduce((sum, move) => sum + move.weight, 0);
      let randomWeight = Math.random() * totalWeight;
      let selectedMove = possibleMoves[0];
      
      for (const move of possibleMoves) {
        randomWeight -= move.weight;
        if (randomWeight <= 0) {
          selectedMove = move;
          break;
        }
      }
      
      const newX = selectedMove.x;
      const newY = selectedMove.y;
      
      console.log("Moving enemy to:", newX, newY);
      
      // Check if we caught the player
      if (newX === playerX && newY === playerY) {
        console.log("Enemy caught player!");
        
        // Set cell shake animation on player cell
        setShakingCell({ x: playerX, y: playerY });
        
        // Show captured animation
        setShowCapturedAnimation(true);
        
        // Show monster attack flash animation
        setShowMonsterAttack(true);
        
        // Update enemy position
        setEnemyPosition({ x: newX, y: newY });
        
        // End game with delay
        setTimeout(() => {
          endGame("lose");
        }, 1000);
        return;
      }
      
      // Update the maze grid
      // First clear the old position
      newMaze[enemyY][enemyX].type = "empty";
      
      // Then set the new position
      newMaze[newY][newX].type = "enemy";
      
      console.log("Updating maze and enemy position...");
      
      // Update enemy position first (to avoid timing issues)
      setEnemyPosition({ x: newX, y: newY });
      
      // Then update the maze state
      setMaze(newMaze);
      
      console.log("Enemy position updated to:", newX, newY);
      
    } catch (error) {
      console.error("Error moving enemy:", error);
    }
  };

  // Handle submit answer (modified to use WebSocket)
  const handleSubmitAnswer = (answer) => {
    if (showFeedback);
    
    setSelectedAnswer(answer);
    
    // Check if we're connected to WebSocket
    if (connected && socket?.readyState === WebSocket.OPEN) {
      submitAnswerToServer(answer);
    } else {
      console.log('Using local answer checking with answer:', answer);
      
      // Fallback to local answer checking
      let correctAnswer = '';
      let isCorrectAnswer = false;
      
      // Handle different formats of correct answer
      if (currentQuestion.correct_answer) {
        if (typeof currentQuestion.correct_answer === 'object' && currentQuestion.correct_answer !== null) {
          correctAnswer = currentQuestion.correct_answer.text || '';
        } else {
          correctAnswer = String(currentQuestion.correct_answer);
        }
      } else if (currentQuestion.correct_option_index !== undefined && 
                Array.isArray(currentQuestion.options)) {
        // If we have a correct_option_index, use that to get the correct answer
        const correctOption = currentQuestion.options[currentQuestion.correct_option_index];
        correctAnswer = typeof correctOption === 'object' ? correctOption.text : String(correctOption);
      }
      
      console.log('Checking against correct answer:', correctAnswer);
      
      // Check if answer is correct
      isCorrectAnswer = answer === correctAnswer;
      setIsCorrect(isCorrectAnswer);
      setShowFeedback(true);
      
      // Update correct/wrong answer count
      if (isCorrectAnswer) {
        setCorrectAnswers(prev => prev + 1);
      } else {
        setWrongAnswers(prev => prev + 1);
      }
    }

    // Handle the answer result after a delay
    setTimeout(() => {
      if (isCorrect) {
        // Move player if answer was correct
        movePlayer();
      }
      
      setShowingQuestion(false);
      setShowFeedback(false);
      setNextMove(null);
    }, 1500);
  };

  // End the game
  const endGame = (result) => {
    setIsGameOver(true);
    setGameResult(result);
    setTimerActive(false);
    
    // Reset animation states after a delay
    setTimeout(() => {
      setShowVictoryAnimation(false);
      setShowCapturedAnimation(false);
      setShowEscapeLight(false);
      setShowMonsterAttack(false);
      setShakingCell(null);
    }, 2000);
    
    // Clear enemy movement timer
    if (enemyMoveTimer) {
      clearInterval(enemyMoveTimer);
    }

    // Notify server about game end if connected
    if (connected && socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'game_ended',
        result: result,
        player_id: playerId,
        player_name: playerName,
        score: correctAnswers * 10
      }));
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    
    if (timerActive) {
      interval = setInterval(() => {
        setTime(prevTime => prevTime + 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive]);

  // Render maze
  const renderMaze = () => {
    const mazeClass = `maze-container ${gameState === 'won' ? 'game-win-animation' : ''} ${gameState === 'lost' ? 'game-loss-animation' : ''}`;
    
    // Helper function to generate random confetti
    const renderConfetti = () => {
      const confettiCount = 50;
      const colors = ['#ff0066', '#00ffff', '#ffff00', '#00ff00', '#ff9900', '#9900ff'];
      
      const confetti = [];
      for (let i = 0; i < confettiCount; i++) {
        const left = Math.random() * 100;
        const animationDelay = Math.random() * 3;
        const color = colors[Math.floor(Math.random() * colors.length)];
        confetti.push(
          <div 
            key={i} 
            className="confetti" 
            style={{
              left: `${left}%`, 
              animationDelay: `${animationDelay}s`,
              backgroundColor: color,
              width: `${Math.random() * 10 + 5}px`,
              height: `${Math.random() * 10 + 5}px`,
              opacity: Math.random() * 0.5 + 0.5
            }}
          />
        );
      }
      return confetti;
    };
    
    return (
      <div className={mazeClass}>
        {/* Victory animations */}
        {showVictoryAnimation && (
          <div className="victory-animation">
            {renderConfetti()}
          </div>
        )}
        
        {/* Escape light animation */}
        {showEscapeLight && (
          <div className="escape-light"></div>
        )}
        
        {/* Captured animation */}
        {showCapturedAnimation && (
          <div className="captured-animation"></div>
        )}
        
        {/* Monster attack flash */}
        {showMonsterAttack && (
          <div className="monster-attack"></div>
        )}
        
        {maze.map((row, y) => (
          <div key={y} className="maze-row">
            {row.map((cell, x) => {
              // Check if this cell contains player or enemy
              const hasPlayer = playerPosition.x === x && playerPosition.y === y;
              const hasEnemy = enemyPosition.x === x && enemyPosition.y === y;
              let cellType = cell.type;
              
              // Override cell display for player and enemy
              if (hasPlayer) cellType = "player";
              if (hasEnemy) cellType = "enemy";
              
              // Check if this cell should be shaking
              const isShaking = shakingCell && shakingCell.x === x && shakingCell.y === y;
              
              return (
                <div
                  key={`${x}-${y}`}
                  className={`maze-cell ${cellType} ${isShaking ? 'cell-shake' : ''}`}
                  title={`(${x},${y})`}
                >
                  {cellType === "player" && <div className="player-icon"></div>}
                  {cellType === "enemy" && <div className="enemy-icon"></div>}
                  {cellType === "exit" && <div className="exit-icon"></div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Render controls
  const renderControls = () => {
    const canMoveUp = availableMoves.some(move => move.dx === 0 && move.dy === -1);
    const canMoveRight = availableMoves.some(move => move.dx === 1 && move.dy === 0);
    const canMoveDown = availableMoves.some(move => move.dx === 0 && move.dy === 1);
    const canMoveLeft = availableMoves.some(move => move.dx === -1 && move.dy === 0);
    
    return (
      <div className="game-controls">
        <Row className="justify-content-center">
          <Col id="up" xs={4} className="text-center">
            <Button
              variant={canMoveUp ? "primary" : "secondary"}
              onClick={() => selectMove(0, -1)}
              disabled={!canMoveUp || showingQuestion}
              aria-label="Move Up"
            >
              <span aria-hidden="true">↑</span>
            </Button>
          </Col>
        </Row>
        <Row className="justify-content-center my-2">
          <Col id="right-left"xs={4} className="d-flex justify-content-between">
            <Button
              variant={canMoveLeft ? "primary" : "secondary"}
              onClick={() => selectMove(-1, 0)}
              disabled={!canMoveLeft || showingQuestion}
              aria-label="Move Left"
            >
              <span aria-hidden="true">←</span>
            </Button>
            <Button
              variant={canMoveRight ? "primary" : "secondary"}
              onClick={() => selectMove(1, 0)}
              disabled={!canMoveRight || showingQuestion}
              aria-label="Move Right"
            >
              <span aria-hidden="true">→</span>
            </Button>
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col id="down" xs={4} className="text-center">
            <Button
              variant={canMoveDown ? "primary" : "secondary"}
              onClick={() => selectMove(0, 1)}
              disabled={!canMoveDown || showingQuestion}
              aria-label="Move Down"
            >
              <span aria-hidden="true">↓</span>
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  // Render question
  const renderQuestion = () => {
    if (!showingQuestion) return null;
    
    // Show loading state while waiting for question from server
    if (!currentQuestion) {
      return (
        <div className="question-modal">
          <Card>
            <Card.Header className="bg-primary text-white">
              <Card.Title>Loading Question...</Card.Title>
              <p className="mb-0">Please wait while we fetch your question!</p>
            </Card.Header>
            <Card.Body className="text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p>Loading your STEM challenge...</p>
            </Card.Body>
          </Card>
        </div>
      );
    }
    
    // Process options based on format (handle all possible formats)
    let processedOptions = [];
    
    // Debug log to help track options format
    console.log('Question options format:', currentQuestion.options, 
                'or choices:', currentQuestion.choices);
    
    if (Array.isArray(currentQuestion.options)) {
      processedOptions = currentQuestion.options.map((option, index) => {
        // If option is an object with text property, use that format
        if (typeof option === 'object' && option !== null) {
          return { 
            id: option.id || option.option_id || String(index),
            text: option.text || option.value || String(option),
            image: option.image || null
          };
        }
        // If option is a string, convert to object format
        return { 
          id: String(index),
          text: String(option),
          image: null
        };
      });
    } else if (currentQuestion.choices && Array.isArray(currentQuestion.choices)) {
      // Handle 'choices' format
      processedOptions = currentQuestion.choices.map((choice, index) => {
        if (typeof choice === 'object' && choice !== null) {
          return {
            id: choice.id || String(index),
            text: choice.text || choice.value || String(choice),
            image: choice.image || null
          };
        }
        return { 
          id: String(index),
          text: String(choice),
          image: null
        };
      });
    } else if (typeof currentQuestion.options === 'object' && currentQuestion.options !== null) {
      // Handle options as object with key-value pairs
      processedOptions = Object.entries(currentQuestion.options).map(([key, value]) => ({
        id: key,
        text: typeof value === 'object' ? (value.text || String(value)) : String(value),
        image: typeof value === 'object' ? value.image : null
      }));
    }
    
    // Ensure we have at least one option
    if (processedOptions.length === 0) {
      console.warn('No options found for question, creating fallback options');
      processedOptions = [
        { id: '1', text: 'Option 1', image: null },
        { id: '2', text: 'Option 2', image: null },
      ];
    }
    
    // Get correct answer text (handling different server response formats)
    let correctAnswerText = '';
    if (typeof currentQuestion.correct_answer === 'object' && currentQuestion.correct_answer !== null) {
      correctAnswerText = currentQuestion.correct_answer.text || '';
    } else if (currentQuestion.correct_answer !== undefined) {
      correctAnswerText = String(currentQuestion.correct_answer);
    }
    
    // Log the correct answer format for debugging
    console.log('Correct answer format:', typeof currentQuestion.correct_answer, 
                'Value:', currentQuestion.correct_answer);
    
    // Get question text from various possible formats
    const questionText = currentQuestion.question || currentQuestion.text || 'Answer the question';
    
    return (
      <div className="question-modal">
        <Card>
          <Card.Header className="bg-primary text-white">
            <Card.Title>STEM Question Challenge</Card.Title>
            <p className="mb-0">Answer correctly to move in your chosen direction!</p>
          </Card.Header>
          <Card.Body>
            <Alert variant="warning">
              The monster moves every {ENEMY_MOVE_INTERVAL/1000} seconds, even while you're answering!
            </Alert>
            
            <h4>{questionText}</h4>
            
            {currentQuestion.image && (
              <div className="text-center mb-3">
                <img 
                  src={currentQuestion.image} 
                  alt="Question" 
                  style={{ maxWidth: '100%', maxHeight: '200px' }} 
                  className="img-fluid"
                />
              </div>
            )}
            
            <div className="options-list">
              {processedOptions.map((option, index) => (
                <Button
                  key={option.id || index}
                  variant={
                    showFeedback
                      ? option.text === correctAnswerText
                        ? "success"
                        : option.text === selectedAnswer && option.text !== correctAnswerText
                        ? "danger"
                        : "outline-secondary"
                      : "outline-primary"
                  }
                  onClick={() => handleSubmitAnswer(option.text)}
                  disabled={showFeedback}
                  className="option-button mb-2"
                  block
                >
                  {option.image && (
                    <img 
                      src={option.image} 
                      alt="" 
                      style={{ height: '30px', marginRight: '10px' }}
                    />
                  )}
                  {option.text}
                </Button>
              ))}
            </div>
            
            {showFeedback && (
              <Alert variant={isCorrect ? "success" : "danger"} className="mt-3">
                <strong>{isCorrect ? "Correct!" : "Incorrect!"}</strong>
                <p>
                  {isCorrect 
                    ? "You will move in your chosen direction." 
                    : "You won't move, but the monster still will!"}
                </p>
                {currentQuestion.explanation && (
                  <div className="mt-2">
                    <strong>Explanation:</strong> {currentQuestion.explanation}
                  </div>
                )}
              </Alert>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Add a game result message component
  const renderGameResultMessage = () => {
    if (gameState === 'playing') return null;
    
    return (
      <div className={`game-result-message ${gameState === 'won' ? 'win-message' : 'loss-message'}`}>
        {gameState === 'won' ? 'You Won!' : 'Game Over!'}
      </div>
    );
  };

  // Add a component to display connected players
  const renderConnectedPlayers = () => {
    if (!connected || players.length === 0) return null;
    
    return (
      <Card className="mb-3">
        <Card.Header>
          <Badge variant="primary">{players.length}</Badge> Connected Players
        </Card.Header>
        <ListGroup variant="flush">
          {players.map(player => (
            <ListGroup.Item key={player.id} className="d-flex justify-content-between align-items-center">
              {player.name}
              {player.score > 0 && (
                <Badge variant="success">{player.score} pts</Badge>
              )}
              {player.id === playerId && (
                <Badge variant="info">You</Badge>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Card>
    );
  };

  // Render topic selector component
  const renderTopicSelector = () => {
    return (
      <Form.Group className="mb-3">
        <Form.Label className="font-weight-bold">Select a Topic:</Form.Label>
        <div className="topic-selector-container" style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '5px' }}>
          {loadingTopics ? (
            <div className="text-center p-3">Loading topics...</div>
          ) : (
            <ListGroup variant="flush">
              <ListGroup.Item 
                action
                active={selectedTopic === ''}
                onClick={() => setSelectedTopic('')}
                className="d-flex justify-content-between align-items-center"
              >
                <span>All Topics (Random Questions)</span>
                {selectedTopic === '' && <Badge variant="primary">Selected</Badge>}
              </ListGroup.Item>
              
              {topics.map(topic => (
                <ListGroup.Item 
                  key={topic.id}
                  action
                  active={selectedTopic === topic.id.toString()}
                  onClick={() => setSelectedTopic(topic.id.toString())}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span>{topic.title}</span>
                  {selectedTopic === topic.id.toString() && <Badge variant="primary">Selected</Badge>}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
        {selectedTopic && (
          <div className="mt-2">
            <Badge variant="info">
              Selected Topic: {topics.find(t => t.id.toString() === selectedTopic)?.title || 'Unknown Topic'}
            </Badge>
          </div>
        )}
      </Form.Group>
    );
  };

  // Render game lobby/welcome screen (modified to add WebSocket connection)
  if (!gameStarted) {
    return (
      <Container className="py-5">
        <Card id="maze-welcome-screen" className="shadow">
          <Card.Header id="maze-welcome-screen" className="bg-primary text-white">
            <Card.Title className="h3">STEM Maze Escape</Card.Title>
            <p className="mb-0">Answer questions to navigate the maze and escape the monster!</p>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="font-weight-bold">Enter Your Name:</Form.Label>
                  <Form.Control
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Your name"
                    className="mb-3"
                  />
                </Form.Group>
                
                {/* Topic Selector */}
                {renderTopicSelector()}
                
                <div className="bg-light p-3 rounded mb-4">
                  <h4 className="text-primary mb-2">How To Play:</h4>
                  <ul className="pl-3">
                    <li>Choose a direction to move by clicking on the arrow buttons</li>
                    <li>Answer a STEM question correctly to move in that direction</li>
                    <li>If you answer incorrectly, you won't move but the enemy will still advance</li>
                    <li>The monster moves one step every {ENEMY_MOVE_INTERVAL/1000} seconds, even during questions</li>
                    <li>Reach the exit (E) to win the game</li>
                    <li>If the monster catches you, you lose!</li>
                  </ul>
                </div>
                
                <div className="bg-light p-3 rounded mb-4">
                  <h4 className="text-primary mb-2">Room Info:</h4>
                  <p><strong>Room ID:</strong> {roomName}</p>
                  <p><strong>Connection Status:</strong> {connected ? 'Connected' : 'Disconnected'}</p>
                </div>
              </Col>
              
              <Col md={4}>
                {/* Display connected players */}
                {renderConnectedPlayers()}
                
                <div className="text-center mt-4">
                  {!connected && (
                    <Button 
                      variant="primary" 
                      className="mb-3" 
                      onClick={connectWebSocket}
                      disabled={!playerName.trim()}
                    >
                      Connect to Game
                    </Button>
                  )}
                  
                  {connected && !gameJoined && (
                    <Button 
                      variant="success" 
                      className="mb-3" 
                      onClick={joinGame}
                      disabled={!playerName.trim()}
                    >
                      Join Game
                    </Button>
                  )}
                  
                  {connected && gameJoined && (
                    <Button 
                      variant="success" 
                      size="lg" 
                      onClick={startGame}
                      className="mt-3"
                    >
                      Start Game
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Render game over screen
  if (isGameOver) {
    return (
      <Container id="maze-end-screen" className="py-5">
        <Card id="maze-end-screen" className="shadow">
          <Card.Header className={`text-white ${gameResult === "win" ? "bg-success" : "bg-danger"}`}>
            <Card.Title className="h3">
              {gameResult === "win" ? "You Escaped! 🎉" : "Game Over!"}
            </Card.Title>
            <p className="mb-0">
              {gameResult === "win" 
                ? "Congratulations! You successfully navigated the maze!" 
                : "Oh no! The monster caught you!"}
            </p>
          </Card.Header>
          <Card.Body>
            {/* Stats */}
            <Row className="mb-4">
              <Col md={4}>
                <div className="bg-light p-3 rounded text-center">
                  <p className="text-muted mb-1">Time</p>
                  <h3>{Math.floor(time / 60)}:{String(time % 60).padStart(2, '0')}</h3>
                </div>
              </Col>
              <Col md={4}>
                <div className="bg-light p-3 rounded text-center">
                  <p className="text-muted mb-1">Correct Answers</p>
                  <h3 className="text-success">{correctAnswers}</h3>
                </div>
              </Col>
              <Col md={4}>
                <div className="bg-light p-3 rounded text-center">
                  <p className="text-muted mb-1">Questions</p>
                  <h3>{correctAnswers + wrongAnswers}</h3>
                  {(correctAnswers + wrongAnswers > 0) && (
                    <small className="text-muted">
                      {Math.round((correctAnswers / (correctAnswers + wrongAnswers)) * 100)}% accuracy
                    </small>
                  )}
                </div>
              </Col>
            </Row>
            
            <Alert variant={gameResult === "win" ? "success" : "danger"} className="text-center mb-4">
              <p className="font-weight-bold">
                {gameResult === "win" 
                  ? `Well done, ${playerName}!` 
                  : `Better luck next time, ${playerName}!`}
              </p>
              <p className="mb-0">
                {gameResult === "win" 
                  ? "You escaped the maze by using your STEM knowledge!" 
                  : "Keep practicing your STEM knowledge and try again!"}
              </p>
            </Alert>
            
            <div className="text-center">
              <Button variant="primary" size="lg" onClick={startGame}>
                Play Again
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Render active game screen (modified to show connected players)
  return (
    <Container fluid className="py-4">
      <Card className="shadow game-card">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0">STEM Maze Escape</Card.Title>
            <Badge pill variant="light">
              Time: {Math.floor(time / 60)}:{String(time % 60).padStart(2, '0')}
            </Badge>
          </div>
          <small>Answer questions to reach the exit before being caught!</small>
        </Card.Header>
        
        <Card.Body className="px-2 px-md-3">
          <Row className="mb-3">
            {/* Game stats (left side) */}
            <Col md={9}>
              <Row className="mb-3 mx-0">
                <Col xs={6} className="px-1">
                  <Alert variant="info" className="py-2 px-2 px-md-3 mb-0 d-flex justify-content-between align-items-center">
                    <span className="small">Position: ({playerPosition.x}, {playerPosition.y})</span>
                    <Badge variant="primary">{playerName}</Badge>
                  </Alert>
                </Col>
                <Col xs={6} className="px-1">
                  <Alert variant="success" className="py-2 px-2 px-md-3 mb-0 d-flex justify-content-between align-items-center">
                    <span className="small">Correct Answers:</span>
                    <Badge variant="success">{correctAnswers}</Badge>
                  </Alert>
                </Col>
              </Row>
              
              <Alert variant="warning" className="py-2 mb-3 small">
                The monster moves one step every {ENEMY_MOVE_INTERVAL/1000} seconds!
              </Alert>
              
              <div className="d-flex justify-content-center">
                {/* Maze */}
                <div className="maze-wrapper w-100 overflow-auto">
                  {renderMaze()}
                </div>
              </div>
              
              {/* Controls */}
              <div className="controls-wrapper position-sticky" style={{ bottom: "10px", zIndex: 50 }}>
                {renderControls()}
              </div>
              
              {/* Legend */}
              <div className="legend mt-3 d-flex justify-content-center flex-wrap">
                <Badge variant="primary" className="m-1">P = Player</Badge>
                <Badge variant="danger" className="m-1">M = Monster</Badge>
                <Badge variant="success" className="m-1">E = Exit</Badge>
                <Badge variant="secondary" className="m-1">■ = Wall</Badge>
              </div>
            </Col>
            
            {/* Player list (right side) */}
            <Col md={3}>
              {renderConnectedPlayers()}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Question modal */}
      {renderQuestion()}

      {/* Game result message */}
      {renderGameResultMessage()}
    </Container>
  );
};

export default MazeGame;
